/**
 * Cuando un link de forjaligas.com ABRE LA APP, llevarla a esa pantalla.
 *
 * Los universal links tienen dos mitades y las dos son necesarias. La de
 * afuera —el archivo `apple-app-site-association` que sirve la web, más el
 * entitlement `associated-domains`— hace que iOS abra FORJA en vez de Safari.
 * Esta es la de adentro: iOS entrega la URL completa al contenedor y, si nadie
 * la escucha, **la app arranca en su pantalla de siempre y el link se pierde**.
 *
 * Y perderse así es peor que no abrir la app: el que mandó la invitación ve que
 * al otro "le abrió FORJA", el otro ve su pantalla de inicio normal y ninguno
 * de los dos entiende por qué el equipo no aparece. Sin este archivo los links
 * de invitación, de hincha y del árbitro funcionan a medias, que es el modo de
 * fallar más difícil de reportar.
 *
 * Se queda con la RUTA y descarta el origen a propósito: el link puede venir
 * del dominio propio o de la URL vieja de Render, y adentro de la app las dos
 * tienen que terminar en la misma pantalla.
 *
 * AL PLUGIN SE LE HABLA POR EL PUENTE, SIN SU PAQUETE JS: el `import()`
 * dinámico de un plugin se colgó para siempre el 3-11 en un iPhone con iOS
 * 26.1 (la historia está en `loginNativo.ts`). Acá el síntoma sería exactamente
 * el del párrafo de arriba —el link abre la app y la app arranca en su pantalla
 * de siempre— y con el `void` de adelante nadie se habría enterado nunca. El
 * paquete queda de plan B.
 */

import { escucharNativo, esNativo, llamarNativo } from "./nativo";

/** El plugin nativo, por su nombre en el puente. */
const PLUGIN = "App";

let enganchado = false;
/**
 * Siempre el ÚLTIMO `irA`. El listener se registra una sola vez pero React
 * llama a esto en cada render con un `navigate` nuevo (con BrowserRouter,
 * `useNavigate` cambia cuando cambia la ruta). Sin esto el listener se
 * quedaría con el `navigate` del primer render para siempre.
 */
let irAActual: ((ruta: string) => void) | null = null;

export function engancharLinksDeLaApp(irA: (ruta: string) => void): void {
  if (!esNativo()) return;
  irAActual = irA;
  if (enganchado) return;
  enganchado = true;

  const alAbrir = (evento: { url: string }) => {
    const ruta = rutaDelLink(evento.url);
    if (ruta) irAActual?.(ruta);
  };
  // ARRANQUE EN FRÍO, que es el caso más común: el jugador nuevo NO tiene
  // la app abierta cuando toca la invitación. Ahí iOS lanza la app con la
  // URL, y `appUrlOpen` puede dispararse ANTES de que este listener exista
  // (el bundle todavía estaba cargando). `getLaunchUrl` devuelve esa URL de
  // lanzamiento para que no se pierda. Un link que abre la app y no lleva a
  // ningún lado es peor que un link que abre Safari.
  const alLanzar = (lanzamiento: { url?: string } | null | undefined) => {
    const ruta = lanzamiento?.url ? rutaDelLink(lanzamiento.url) : null;
    if (ruta) irAActual?.(ruta);
  };

  if (escucharNativo<{ url: string }>(PLUGIN, "appUrlOpen", alAbrir)) {
    // Sin URL de lanzamiento el plugin resuelve vacío; un rechazo es arranque normal.
    void llamarNativo<{ url?: string }>(PLUGIN, "getLaunchUrl")
      ?.then(alLanzar)
      .catch(() => undefined);
    return;
  }

  void import("@capacitor/app").then(async ({ App }) => {
    void App.addListener("appUrlOpen", alAbrir);
    try {
      alLanzar(await App.getLaunchUrl());
    } catch {
      /* sin URL de lanzamiento: arranque normal */
    }
  });
}

/**
 * De la URL completa a la ruta interna. Devuelve `null` cuando no hay nada que
 * hacer, que incluye un caso que NO es un link: el esquema de vuelta del
 * ingreso con Google (`com.googleusercontent.apps.…`) llega por este mismo
 * canal y lo tiene que atender el plugin, no el router. Navegar ahí rompería
 * el ingreso justo cuando está por terminar.
 */
export function rutaDelLink(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    const ruta = `${u.pathname}${u.search}${u.hash}`;
    if (!ruta || ruta === "/") return null;
    return ruta;
  } catch {
    return null;
  }
}
